import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { getCountryByCode } from '../data/countries';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  Building, Package, ShoppingCart, MessageSquare, Users,
  TrendingUp, Globe, Award, Clock, Loader2, Eye, MapPin, Smartphone, Monitor, Tablet
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#2563eb', '#16a34a', '#dc2626', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1'];

export default function AdminAnalytics() {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [visitData, setVisitData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [analyticsRes, visitsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/visits`).catch(() => ({ data: null }))
      ]);
      setData(analyticsRes.data);
      setVisitData(visitsRes.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="analytics-loading">
        <Loader2 size={32} className="animate-spin text-accent" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, country_distribution, monthly_growth, top_companies, recent_activity } = data;

  // Prepare status data for pie charts
  const companyStatusData = [
    { name: language === 'ar' ? 'معتمد' : 'Approved', value: summary.companies.approved },
    { name: language === 'ar' ? 'معلق' : 'Pending', value: summary.companies.pending },
    { name: language === 'ar' ? 'مرفوض' : 'Rejected', value: summary.companies.rejected },
    { name: language === 'ar' ? 'موقوف' : 'Suspended', value: summary.companies.suspended },
  ].filter(d => d.value > 0);

  const offerStatusData = [
    { name: language === 'ar' ? 'معتمد' : 'Approved', value: summary.offers.approved },
    { name: language === 'ar' ? 'معلق' : 'Pending', value: summary.offers.pending },
    { name: language === 'ar' ? 'مرفوض' : 'Rejected', value: summary.offers.rejected },
  ].filter(d => d.value > 0);

  const requestStatusData = [
    { name: language === 'ar' ? 'معتمد' : 'Approved', value: summary.requests.approved },
    { name: language === 'ar' ? 'معلق' : 'Pending', value: summary.requests.pending },
    { name: language === 'ar' ? 'مرفوض' : 'Rejected', value: summary.requests.rejected },
  ].filter(d => d.value > 0);

  // Prepare country data with names
  const countryChartData = country_distribution.map(item => {
    const c = getCountryByCode(item.country);
    return {
      name: c ? (language === 'ar' ? c.name_ar : c.name_en) : item.country,
      flag: c ? c.flag : '',
      count: item.count
    };
  });

  // Month labels
  const monthLabels = {
    '01': language === 'ar' ? 'يناير' : 'Jan',
    '02': language === 'ar' ? 'فبراير' : 'Feb',
    '03': language === 'ar' ? 'مارس' : 'Mar',
    '04': language === 'ar' ? 'أبريل' : 'Apr',
    '05': language === 'ar' ? 'مايو' : 'May',
    '06': language === 'ar' ? 'يونيو' : 'Jun',
    '07': language === 'ar' ? 'يوليو' : 'Jul',
    '08': language === 'ar' ? 'أغسطس' : 'Aug',
    '09': language === 'ar' ? 'سبتمبر' : 'Sep',
    '10': language === 'ar' ? 'أكتوبر' : 'Oct',
    '11': language === 'ar' ? 'نوفمبر' : 'Nov',
    '12': language === 'ar' ? 'ديسمبر' : 'Dec',
  };

  const growthData = monthly_growth.map(item => ({
    ...item,
    label: monthLabels[item.month.split('-')[1]] || item.month
  }));

  const statusColors = ['#16a34a', '#f59e0b', '#dc2626', '#f97316'];

  const getActivityIcon = (type) => {
    if (type === 'company') return <Building size={16} className="text-accent" />;
    if (type === 'offer') return <Package size={16} className="text-blue-600" />;
    return <ShoppingCart size={16} className="text-green-600" />;
  };

  const getActivityLabel = (type) => {
    if (type === 'company') return language === 'ar' ? 'شركة' : 'Company';
    if (type === 'offer') return language === 'ar' ? 'عرض' : 'Offer';
    return language === 'ar' ? 'طلب' : 'Request';
  };

  const getStatusColor = (status) => {
    const map = { approved: 'text-green-600', pending: 'text-yellow-600', rejected: 'text-red-600', suspended: 'text-orange-600' };
    return map[status] || 'text-gray-600';
  };

  const getStatusLabel = (status) => {
    const map = {
      approved: language === 'ar' ? 'معتمد' : 'Approved',
      pending: language === 'ar' ? 'معلق' : 'Pending',
      rejected: language === 'ar' ? 'مرفوض' : 'Rejected',
      suspended: language === 'ar' ? 'موقوف' : 'Suspended'
    };
    return map[status] || status;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-border text-sm">
        <p className="font-semibold text-primary mb-1">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color }} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: entry.color }} />
            {entry.name}: <span className="font-bold">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <SummaryCard
          icon={<Building size={22} />}
          iconColor="text-accent"
          iconBg="bg-accent/10"
          label={language === 'ar' ? 'الشركات' : 'Companies'}
          value={summary.companies.total}
          testId="analytics-companies-total"
        />
        <SummaryCard
          icon={<Package size={22} />}
          iconColor="text-blue-600"
          iconBg="bg-blue-100"
          label={language === 'ar' ? 'عروض البيع' : 'Sale Offers'}
          value={summary.offers.total}
          testId="analytics-offers-total"
        />
        <SummaryCard
          icon={<ShoppingCart size={22} />}
          iconColor="text-green-600"
          iconBg="bg-green-100"
          label={language === 'ar' ? 'طلبات الشراء' : 'Buy Requests'}
          value={summary.requests.total}
          testId="analytics-requests-total"
        />
        <SummaryCard
          icon={<MessageSquare size={22} />}
          iconColor="text-purple-600"
          iconBg="bg-purple-100"
          label={language === 'ar' ? 'الرسائل' : 'Messages'}
          value={summary.messages}
          testId="analytics-messages-total"
        />
        <SummaryCard
          icon={<Users size={22} />}
          iconColor="text-teal-600"
          iconBg="bg-teal-100"
          label={language === 'ar' ? 'المستخدمين' : 'Users'}
          value={summary.users}
          testId="analytics-users-total"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Growth */}
        <div className="bg-white p-5 sm:p-6 border border-border rounded-xl" data-testid="analytics-monthly-growth">
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp size={20} className="text-accent" />
            <h3 className="text-base sm:text-lg font-heading font-bold text-primary">
              {language === 'ar' ? 'النمو الشهري' : 'Monthly Growth'}
            </h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={growthData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="companies" name={language === 'ar' ? 'شركات' : 'Companies'} fill="#2563eb" radius={[4, 4, 0, 0]} />
                <Bar dataKey="offers" name={language === 'ar' ? 'عروض' : 'Offers'} fill="#16a34a" radius={[4, 4, 0, 0]} />
                <Bar dataKey="requests" name={language === 'ar' ? 'طلبات' : 'Requests'} fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Country Distribution */}
        <div className="bg-white p-5 sm:p-6 border border-border rounded-xl" data-testid="analytics-country-distribution">
          <div className="flex items-center gap-2 mb-5">
            <Globe size={20} className="text-accent" />
            <h3 className="text-base sm:text-lg font-heading font-bold text-primary">
              {language === 'ar' ? 'توزيع الدول' : 'Country Distribution'}
            </h3>
          </div>
          {countryChartData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={countryChartData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name={language === 'ar' ? 'شركات' : 'Companies'} fill="#2563eb" radius={[0, 4, 4, 0]}>
                    {countryChartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
              {language === 'ar' ? 'لا توجد بيانات' : 'No data available'}
            </div>
          )}
        </div>
      </div>

      {/* Status Pie Charts */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <StatusPieChart
          title={language === 'ar' ? 'حالة الشركات' : 'Companies Status'}
          data={companyStatusData}
          colors={statusColors}
          testId="analytics-company-status-pie"
        />
        <StatusPieChart
          title={language === 'ar' ? 'حالة العروض' : 'Offers Status'}
          data={offerStatusData}
          colors={statusColors}
          testId="analytics-offer-status-pie"
        />
        <StatusPieChart
          title={language === 'ar' ? 'حالة الطلبات' : 'Requests Status'}
          data={requestStatusData}
          colors={statusColors}
          testId="analytics-request-status-pie"
        />
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Companies */}
        <div className="bg-white p-5 sm:p-6 border border-border rounded-xl" data-testid="analytics-top-companies">
          <div className="flex items-center gap-2 mb-5">
            <Award size={20} className="text-accent" />
            <h3 className="text-base sm:text-lg font-heading font-bold text-primary">
              {language === 'ar' ? 'أنشط الشركات' : 'Most Active Companies'}
            </h3>
          </div>
          {top_companies.length > 0 ? (
            <div className="space-y-3">
              {top_companies.map((company, i) => {
                const c = getCountryByCode(company.country);
                return (
                  <div key={company.name} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`top-company-${i}`}>
                    <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-sm font-bold text-accent flex-shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-primary text-sm truncate">{company.name}</p>
                      {c && <p className="text-xs text-muted-foreground">{c.flag} {language === 'ar' ? c.name_ar : c.name_en}</p>}
                    </div>
                    <div className="flex gap-3 text-xs flex-shrink-0">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                        {company.offers} {language === 'ar' ? 'عرض' : 'offers'}
                      </span>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                        {company.requests} {language === 'ar' ? 'طلب' : 'requests'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-10 text-center text-muted-foreground text-sm">
              {language === 'ar' ? 'لا توجد بيانات' : 'No data available'}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white p-5 sm:p-6 border border-border rounded-xl" data-testid="analytics-recent-activity">
          <div className="flex items-center gap-2 mb-5">
            <Clock size={20} className="text-accent" />
            <h3 className="text-base sm:text-lg font-heading font-bold text-primary">
              {language === 'ar' ? 'آخر النشاطات' : 'Recent Activity'}
            </h3>
          </div>
          {recent_activity.length > 0 ? (
            <div className="space-y-3">
              {recent_activity.map((activity, i) => (
                <div key={`${activity.type}-${activity.name}-${i}`} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`recent-activity-${i}`}>
                  <div className="mt-0.5 flex-shrink-0">{getActivityIcon(activity.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-primary truncate">{activity.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">{getActivityLabel(activity.type)}</span>
                      <span className={`text-xs font-medium ${getStatusColor(activity.status)}`}>{getStatusLabel(activity.status)}</span>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                    {new Date(activity.date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-10 text-center text-muted-foreground text-sm">
              {language === 'ar' ? 'لا توجد نشاطات حديثة' : 'No recent activity'}
            </div>
          )}
        </div>
      </div>

      {/* Site Visits Section */}
      {visitData && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Eye size={20} className="text-accent" />
            <h3 className="text-base sm:text-lg font-heading font-bold text-primary">
              {language === 'ar' ? 'زيارات الموقع' : 'Site Visits'}
            </h3>
          </div>

          {/* Visit Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white p-4 border border-border rounded-xl" data-testid="visits-total">
              <p className="text-xs text-muted-foreground mb-1">{language === 'ar' ? 'إجمالي الزيارات' : 'Total Visits'}</p>
              <p className="text-2xl font-heading font-bold text-primary">{visitData.total_visits.toLocaleString()}</p>
            </div>
            <div className="bg-white p-4 border border-border rounded-xl" data-testid="visits-today">
              <p className="text-xs text-muted-foreground mb-1">{language === 'ar' ? 'زيارات اليوم' : 'Today'}</p>
              <p className="text-2xl font-heading font-bold text-accent">{visitData.today_visits.toLocaleString()}</p>
            </div>
            <div className="bg-white p-4 border border-border rounded-xl" data-testid="visits-today-unique">
              <p className="text-xs text-muted-foreground mb-1">{language === 'ar' ? 'زوار اليوم الفريدين' : 'Unique Today'}</p>
              <p className="text-2xl font-heading font-bold text-green-600">{visitData.today_unique.toLocaleString()}</p>
            </div>
            <div className="bg-white p-4 border border-border rounded-xl" data-testid="visits-week-unique">
              <p className="text-xs text-muted-foreground mb-1">{language === 'ar' ? 'زوار الأسبوع' : 'Weekly Unique'}</p>
              <p className="text-2xl font-heading font-bold text-purple-600">{visitData.week_unique.toLocaleString()}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Visits Chart */}
            {visitData.daily_visits && visitData.daily_visits.length > 0 && (
              <div className="bg-white p-5 sm:p-6 border border-border rounded-xl" data-testid="visits-daily-chart">
                <div className="flex items-center gap-2 mb-5">
                  <TrendingUp size={18} className="text-accent" />
                  <h4 className="text-sm font-heading font-bold text-primary">
                    {language === 'ar' ? 'الزيارات اليومية (آخر 7 أيام)' : 'Daily Visits (Last 7 Days)'}
                  </h4>
                </div>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={visitData.daily_visits.map(d => ({
                      ...d,
                      label: d.date.slice(5)
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="visits" name={language === 'ar' ? 'زيارات' : 'Visits'} fill="#2563eb" radius={[4,4,0,0]} />
                      <Bar dataKey="unique_visitors" name={language === 'ar' ? 'زوار فريدين' : 'Unique'} fill="#16a34a" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Visits by Country */}
            {visitData.country_distribution && visitData.country_distribution.length > 0 && (
              <div className="bg-white p-5 sm:p-6 border border-border rounded-xl" data-testid="visits-country">
                <div className="flex items-center gap-2 mb-5">
                  <MapPin size={18} className="text-accent" />
                  <h4 className="text-sm font-heading font-bold text-primary">
                    {language === 'ar' ? 'الزيارات حسب الدولة' : 'Visits by Country'}
                  </h4>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {visitData.country_distribution.map((item, i) => {
                    const c = getCountryByCode(item.country);
                    const maxVisits = visitData.country_distribution[0]?.visits || 1;
                    const pct = Math.round((item.visits / maxVisits) * 100);
                    return (
                      <div key={item.country} className="flex items-center gap-3" data-testid={`visit-country-${item.country}`}>
                        <span className="text-lg w-7 text-center">{c ? c.flag : '🌍'}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-foreground truncate">
                              {c ? (language === 'ar' ? c.name_ar : c.name_en) : item.country}
                            </span>
                            <span className="text-xs text-muted-foreground ml-2">
                              {item.visits} {language === 'ar' ? 'زيارة' : 'visits'} · {item.unique_visitors} {language === 'ar' ? 'زائر' : 'unique'}
                            </span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-1.5">
                            <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Top Pages & Device Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Device Breakdown */}
            {visitData.device_breakdown && visitData.device_breakdown.length > 0 && (
              <div className="bg-white p-5 sm:p-6 border border-border rounded-xl" data-testid="visits-devices">
                <div className="flex items-center gap-2 mb-5">
                  <Smartphone size={18} className="text-accent" />
                  <h4 className="text-sm font-heading font-bold text-primary">
                    {language === 'ar' ? 'نوع الجهاز' : 'Device Type'}
                  </h4>
                </div>
                {(() => {
                  const deviceLabels = {
                    mobile: { ar: 'موبايل', en: 'Mobile', icon: <Smartphone size={20} />, color: '#2563eb', bg: 'bg-blue-50' },
                    desktop: { ar: 'كمبيوتر', en: 'Desktop', icon: <Monitor size={20} />, color: '#16a34a', bg: 'bg-green-50' },
                    tablet: { ar: 'تابلت', en: 'Tablet', icon: <Tablet size={20} />, color: '#f59e0b', bg: 'bg-amber-50' },
                    bot: { ar: 'بوت', en: 'Bot', icon: <Globe size={20} />, color: '#94a3b8', bg: 'bg-gray-50' },
                    unknown: { ar: 'غير معروف', en: 'Unknown', icon: <Globe size={20} />, color: '#94a3b8', bg: 'bg-gray-50' },
                  };
                  const total = visitData.device_breakdown.reduce((s, d) => s + d.count, 0) || 1;
                  return (
                    <div className="space-y-3">
                      {visitData.device_breakdown.map((d) => {
                        const cfg = deviceLabels[d.device] || deviceLabels.unknown;
                        const pct = Math.round((d.count / total) * 100);
                        return (
                          <div key={d.device} className="flex items-center gap-3" data-testid={`device-${d.device}`}>
                            <div className={`w-10 h-10 ${cfg.bg} rounded-lg flex items-center justify-center`} style={{ color: cfg.color }}>
                              {cfg.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-foreground">
                                  {language === 'ar' ? cfg.ar : cfg.en}
                                </span>
                                <span className="text-sm font-bold" style={{ color: cfg.color }}>{pct}%</span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2">
                                <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: cfg.color }} />
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">{d.count.toLocaleString()} {language === 'ar' ? 'زيارة' : 'visits'}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Top Pages */}
            {visitData.top_pages && visitData.top_pages.length > 0 && (
              <div className="bg-white p-5 sm:p-6 border border-border rounded-xl" data-testid="visits-top-pages">
                <h4 className="text-sm font-heading font-bold text-primary mb-4">
                  {language === 'ar' ? 'الصفحات الأكثر زيارة' : 'Top Pages'}
                </h4>
                <div className="space-y-2">
                  {visitData.top_pages.map((p, i) => (
                    <div key={p.page} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-foreground truncate flex-1">{p.page}</span>
                      <span className="text-sm font-bold text-accent ml-3">{p.visits}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon, iconColor, iconBg, label, value, testId }) {
  return (
    <div className="bg-white p-4 sm:p-5 border border-border rounded-xl" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center ${iconColor}`}>
          {icon}
        </div>
        <span className="text-2xl sm:text-3xl font-heading font-bold text-primary">{value}</span>
      </div>
      <p className="text-xs sm:text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function StatusPieChart({ title, data, colors, testId }) {
  return (
    <div className="bg-white p-5 sm:p-6 border border-border rounded-xl" data-testid={testId}>
      <h3 className="text-sm sm:text-base font-heading font-bold text-primary mb-3 text-center">{title}</h3>
      {data.length > 0 ? (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={35}
                outerRadius={65}
                paddingAngle={3}
                dataKey="value"
                label={({ name, value }) => `${name} (${value})`}
                labelLine={{ strokeWidth: 1 }}
              >
                {data.map((_, i) => (
                  <Cell key={i} fill={colors[i % colors.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
          --
        </div>
      )}
    </div>
  );
}
